import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import * as CoreActions from '@core/core.actions';
import { selectActiveUsers, selectAdminCustomerSpace } from '@core/core.selectors';
import { CustomerSpaceAdmin, CustomerSpaceUser, Role, User } from '@core/core.state';
import { CustomerSpaceUsersTableComponent } from '@core/customer-space-admin/feature/customer-space-users-table.component';
import { PostCustomerSpaceDto, PostCustomerSpaceUserDto } from '@core/customer-space/customer-space.model';
import { UpdateEmployeeDialogComponent } from '@core/update-employee-dialog/update-employee-dialog.component';
import { UserDto } from '@core/users/user.model';
import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { AuthorizeService } from '@platform/auth/authorize.service';
import { ConfirmDialogComponent } from '@platform/components/confirm-dialog/confirm-dialog.component';
import { InfoDialogComponent } from '@platform/components/info-dialog/info-dialog.component';
import { DialogV2Service } from '@platform/dialog/dialog-v2.service';
import { DialogService } from '@platform/dialog/dialog.service';
import { selectPortalConfig } from '@platform/platform.selectors';
import * as RouterActions from '@platform/router.actions';
import { Error, ResourceState, ResourceStatuses } from '@platform/shared/resource.state';
import { RootState } from '@root/app.state';
import { Observable, filter, map, pairwise } from 'rxjs';

@Component({
  selector: 'app-customer-space-admin-v2',
  templateUrl: './customer-space-admin-v2.component.html',
  styleUrls: ['./customer-space-admin-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CustomerSpaceAdminV2Component implements OnInit {
  @ViewChild(CustomerSpaceUsersTableComponent)
  public innerTable: CustomerSpaceUsersTableComponent;

  public customerForm: UntypedFormGroup;
  public abacusInstances$!: Observable<string[]>;
  public customerSpaceUsers!: CustomerSpaceUser[];
  public savePending$!: Observable<boolean>;
  public customerSpaceId!: string;
  public isCustomerSpaceActive = true;
  public isPortalAdministrator = false;
  public deactivatedBy!: string;
  public deactivationTime!: Date;

  randomName1 = Math.random().toString();

  private customerSpaceRoles: Role[] = [];
  private activeUsers: User[] = [];
  private projectTitles: string[] = [];
  private userToDelete!: User;
  private isActiveForExternalControl: AbstractControl<boolean>;

  constructor(
    private translate: TranslateService,
    private store$: Store<RootState>,
    private formBuilder: UntypedFormBuilder,
    private dialogV1Service: DialogService,
    private dialogService: DialogV2Service,
    private authorizeService: AuthorizeService,
    private destroyRef: DestroyRef,
  ) {
    this.customerForm = this.formBuilder.group(
      {
        name: ['', [Validators.required, Validators.maxLength(200)]],
        erpAddressNumber: ['', [Validators.required, Validators.maxLength(20)]],
        erpProjectNumber1: ['', [Validators.required, Validators.maxLength(20), Validators.pattern('[^;]*')]],
        erpProjectNumber2: ['', [Validators.maxLength(20), Validators.pattern('[^;]*')]],
        erpProjectNumber3: ['', [Validators.maxLength(20), Validators.pattern('[^;]*')]],
        erpProjectNumber4: ['', [Validators.maxLength(20), Validators.pattern('[^;]*')]],
        abacusInstanceId: [{ value: '', disabled: !this.isPortalAdministrator }, [Validators.maxLength(50)]],
        abacusMandatorId: [{ value: '', disabled: !this.isPortalAdministrator }, [Validators.maxLength(50)]],
        status: ['Active', [Validators.required]],
        isActiveForExternal: [true, [Validators.required]],
        deactivationComment: [''],
        isTermsUpdateRequired: [{ value: false, disabled: true }],
        // filterUsersByCustomerSpace: [''],
      },
      {
        validators: [this.companyFormValidator()],
      },
    );

    this.isActiveForExternalControl = this.customerForm.controls['isActiveForExternal'];
  }

  ngOnInit(): void {
    this.subscribeFormChanges();

    this.store$
      .pipe(select(selectAdminCustomerSpace), takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => this.onCustomerSpaceLoaded(state));

    this.abacusInstances$ = this.store$.pipe(
      select(selectPortalConfig),
      filter((config) => !!config),
      map((config) => config!.abacusInstances),
    );

    this.savePending$ = this.store$.pipe(
      select(selectAdminCustomerSpace),
      map((state) => state.status === ResourceStatuses.PENDING),
    );

    this.store$
      .pipe(
        select(selectActiveUsers),
        filter((users) => users.status === ResourceStatuses.SUCCEEDED && !!users.data),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((users) => (this.activeUsers = users.data));

    this.authorizeService.isAuthorized('CUSTOMER_SPACE_CREATE', new Map<string, string>()).subscribe((isAuthorized) => {
      this.isPortalAdministrator = isAuthorized;
      this.disableErpNumbers();
      this.onAbacusInstanceChanged(this.customerForm.get('abacusInstanceId')!.value);

      // fix warning "It looks like you're using the disabled attribute".
      if (this.isPortalAdministrator) {
        console.log('CustomerSpaceAdminComponent. Enabling abacusIds for portal admin.');
        this.customerForm.get('abacusInstanceId')?.enable();
        this.customerForm.get('abacusMandatorId')?.enable();
      }
    });
  }

  public saveCustomer($event: any) {
    if ($event.target.type === 'textarea') {
      return;
    }
    $event.preventDefault();
    $event.stopPropagation();

    if (!this.customerForm.valid) {
      return;
    }

    const isActive = this.customerForm.get('status')!.value === 'Active';

    if (this.isCustomerSpaceActive && !isActive) {
      this.showSetInactiveConfirmationDialog();
      return;
    }

    if (!this.isCustomerSpaceActive && isActive) {
      this.showSetActiveConfirmationDialog();
      return;
    }

    this.storeCustomerSpaceData();
  }

  public deleteEmployee(user: CustomerSpaceUser) {
    this.userToDelete = user;
    this.dialogService
      .open(ConfirmDialogComponent, {
        width: '700px',
        autoFocus: false,
        disableClose: true,
        data: {
          titleTranslationKey: 'customer-spaces.user-management.remove-employee.title',
          textTranslationKey: 'customer-spaces.user-management.remove-employee.confirm-question',
        },
      })
      .afterClosed()
      .pipe(filter((data) => !!data?.isConfirmed))
      .subscribe(() => this.removeUserFromCustomerSpace());
  }

  public addOrUpdateEmployee(data: any): void {
    if (!data) {
      return;
    }

    const updateUserDto: PostCustomerSpaceUserDto = {
      roleId: data.selectedRoleId,
      userId: data.selectedEmployee.id,
      isContact: data.selectedIsContact,
      isMailSent: data.selectedIsMailSent,
      roleDescription: data.selectedRoleDescription,
      appPermissions: data.appPermissions,
      abacusMandatorId: this.customerForm.get('abacusMandatorId')!.value,
    };

    this.store$.dispatch(
      CoreActions.updateCustomerSpaceUser({
        customerSpaceId: this.customerSpaceId,
        updateUser: updateUserDto,
      }),
    );
  }

  public openAddEmployeeDialog(): void {
    if (this.customerSpaceId) {
      this.openEmployeeDialog(null!);
    }
  }

  public openEditEmployeeDialog(user: CustomerSpaceUser): void {
    this.openEmployeeDialog(user);
  }

  public tryDeleteCustomerSpace(): void {
    if (!this.customerSpaceId || this.isCustomerSpaceActive) {
      return;
    }

    if (this.projectTitles.length > 0) {
      this.openDeleteInfoDialog();
      return;
    }

    this.openDeleteConfirmDialog();
  }

  navigateBack() {
    this.store$.dispatch(RouterActions.back());
  }

  companyFormValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const abacusInstanceId = control.get('abacusInstanceId')!.value;
      const abacusMandatorId = control.get('abacusMandatorId')!.value;

      const abacusInstanceHasValue = !!abacusInstanceId && abacusInstanceId !== '';
      const abacusMandatorHasValue = !!abacusMandatorId && abacusMandatorId !== '';

      if ((abacusInstanceHasValue && !abacusMandatorHasValue) || (!abacusInstanceHasValue && abacusMandatorHasValue)) {
        return { abacusInvalid: true };
      }

      if (control.get('status')!.value === 'Inactive' && !control.get('deactivationComment')!.value) {
        control.get('deactivationComment')!.setErrors({ deactivationCommentRequired: true });
      } else {
        control.get('deactivationComment')!.setErrors(null);
      }

      return null;
    };
  }

  public openEmployeeDialog(user: CustomerSpaceUser) {
    this.dialogV1Service
      .openDialog(UpdateEmployeeDialogComponent, {
        width: '900px',
        autoFocus: false,
        disableClose: true,
        data: {
          activeUsers: this.getActiveUsersNotInCustomerSpace(),
          customerSpaceRoles: this.customerSpaceRoles,
          selectedEmployee: user,
          customerSpaceId: this.customerSpaceId,
        },
      })
      .pipe(filter((data) => !!data))
      .subscribe((data) => this.addOrUpdateEmployee(data));
  }

  private showSetInactiveConfirmationDialog() {
    this.dialogService
      .open(ConfirmDialogComponent, {
        width: '700px',
        autoFocus: false,
        disableClose: true,
        data: {
          titleTranslationKey: 'customer-spaces.set-inactive.title',
          textTranslationKey: 'customer-spaces.set-inactive.confirm-message',
        },
      })
      .afterClosed()
      .pipe(filter((data) => !!data?.isConfirmed))
      .subscribe(() => this.storeCustomerSpaceData());
  }

  private showSetActiveConfirmationDialog() {
    this.dialogService
      .open(ConfirmDialogComponent, {
        width: '700px',
        autoFocus: false,
        disableClose: true,
        data: {
          titleTranslationKey: 'customer-spaces.set-active.title',
          textTranslationKey: 'customer-spaces.set-active.confirm-message',
        },
      })
      .afterClosed()
      .pipe(filter((data) => !!data?.isConfirmed))
      .subscribe(() => this.storeCustomerSpaceData());
  }

  private storeCustomerSpaceData() {
    const customerSpace: PostCustomerSpaceDto = {
      name: this.customerForm.get('name')!.value,
      erpAddressNumber: this.customerForm.get('erpAddressNumber')!.value === '' ? null : this.customerForm.get('erpAddressNumber')!.value,
      erpProjectNumbers: [
        this.customerForm.get('erpProjectNumber1')!.value,
        this.customerForm.get('erpProjectNumber2')!.value,
        this.customerForm.get('erpProjectNumber3')!.value,
        this.customerForm.get('erpProjectNumber4')!.value,
      ]
        .filter((x) => x !== '')
        .join(';'),
      abacusInstanceId: this.customerForm.get('abacusInstanceId')!.value,
      abacusMandatorId: this.customerForm.get('abacusMandatorId')!.value,
      isActive: this.customerForm.get('status')!.value === 'Active',
      isActiveForExternal: this.customerForm.get('isActiveForExternal')!.value,
      deactivationComment: this.customerForm.get('deactivationComment')!.value,
    };

    if (this.customerSpaceId) {
      this.store$.dispatch(CoreActions.updateCustomerSpace({ id: this.customerSpaceId, customerSpace }));
    } else {
      this.store$.dispatch(CoreActions.createCustomerSpace({ customerSpace }));
    }
  }

  private deleteCustomerSpace() {
    this.store$.dispatch(CoreActions.deleteCustomerSpace({ customerSpaceId: this.customerSpaceId }));
  }

  private removeUserFromCustomerSpace() {
    this.store$.dispatch(
      CoreActions.removeCustomerSpaceUser({
        customerSpaceId: this.customerSpaceId,
        userId: this.userToDelete.id,
      }),
    );
  }

  private openDeleteInfoDialog() {
    let blockerInfo = this.translate.instant('customer-spaces.delete-customer.deletion-blocked');
    blockerInfo += '\n';

    blockerInfo += this.projectTitles.join('\n');

    this.dialogV1Service.openDialog(InfoDialogComponent, {
      width: '700px',
      autoFocus: false,
      disableClose: true,
      data: {
        title: 'customer-spaces.delete-customer.deletion-blocked-title',
        text: blockerInfo,
      },
    });
  }

  private openDeleteConfirmDialog() {
    this.dialogService
      .open(ConfirmDialogComponent, {
        width: '700px',
        autoFocus: false,
        disableClose: true,
        data: {
          titleTranslationKey: 'customer-spaces.delete-customer.title',
          textTranslationKey: 'customer-spaces.delete-customer.confirm-question',
        },
      })
      .afterClosed()
      .pipe(filter((data) => !!data?.isConfirmed))
      .subscribe(() => this.deleteCustomerSpace());
  }

  private getActiveUsersNotInCustomerSpace(): UserDto[] {
    const customerSpaceUserIds = this.customerSpaceUsers.filter((u) => !u.isDeputy).map((u) => u.id);

    return this.activeUsers.filter((user) => customerSpaceUserIds.indexOf(user.id) < 0);
  }

  private subscribeFormChanges() {
    this.customerForm.get('abacusMandatorId')!.disable();

    this.customerForm
      .get('abacusInstanceId')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((abacusInstanceId) => this.onAbacusInstanceChanged(abacusInstanceId));

    this.subscribeForIsActiveForExternalChangesToShowConfirmationDialog();
  }

  private subscribeForIsActiveForExternalChangesToShowConfirmationDialog() {
    this.isActiveForExternalControl.valueChanges
      .pipe(
        pairwise(), // Get [previousValue, currentValue]
      )
      .subscribe(([prev, current]) => {
        if (prev === true && current === false) {
          this.dialogService
            .open(ConfirmDialogComponent, {
              width: '700px',
              autoFocus: false,
              disableClose: true,
              data: {
                titleTranslationKey: 'customer-spaces.set-inactive-for-external.title',
                textTranslationKey: 'customer-spaces.set-inactive-for-external.confirm-message',
                btnOkTranslationKey: 'shared.yes',
                btnCancelTranslationKey: 'shared.no',
              },
            })
            .afterClosed()
            .pipe(
              map((data) => !!data?.isConfirmed),
              takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((isConfirmed) => {
              if (!isConfirmed) {
                // Revert to the previous value with (or without if false) triggering a new event
                // jconsole.log('reverting to prev', prev);
                this.isActiveForExternalControl.setValue(prev, { emitEvent: true });
              }
            });
        }
      });
  }

  private onAbacusInstanceChanged(abacusInstanceId: string) {
    if (!this.isCustomerSpaceActive || !this.isPortalAdministrator) {
      this.customerForm.get('abacusMandatorId')!.disable();
      return;
    }

    if (abacusInstanceId === '' || !abacusInstanceId) {
      this.customerForm.get('abacusMandatorId')!.reset();
      this.customerForm.get('abacusMandatorId')!.disable();
    } else {
      this.customerForm.get('abacusMandatorId')!.enable();
    }
  }

  private onCustomerSpaceLoaded(state: ResourceState<CustomerSpaceAdmin>) {
    if (state.data) {
      if (state.status === ResourceStatuses.SUCCEEDED) {
        this.customerSpaceId = state.data.id;
        this.isCustomerSpaceActive = state.data.isActive;

        if (state.data.isActive) {
          this.customerForm.get('abacusInstanceId')!.enable();
          this.customerForm.get('abacusMandatorId')!.enable();
          this.customerForm.get('deactivationComment')!.enable();
          this.customerForm.get('name')!.enable();
        } else {
          this.customerForm.get('abacusInstanceId')!.disable();
          this.customerForm.get('abacusMandatorId')!.disable();
          this.customerForm.get('deactivationComment')!.disable();
          this.customerForm.get('name')!.disable();
        }

        const erpProjectNumberArray = state.data.erpProjectNumbers?.split(';') ?? [];

        this.customerForm.setValue({
          name: state.data.name,
          erpAddressNumber: state.data.erpAddressNumber ?? '',
          erpProjectNumber1: erpProjectNumberArray.length > 0 ? erpProjectNumberArray[0] : '',
          erpProjectNumber2: erpProjectNumberArray.length > 1 ? erpProjectNumberArray[1] : '',
          erpProjectNumber3: erpProjectNumberArray.length > 2 ? erpProjectNumberArray[2] : '',
          erpProjectNumber4: erpProjectNumberArray.length > 3 ? erpProjectNumberArray[3] : '',
          abacusInstanceId: state.data.abacusMandator.abacusInstanceId,
          abacusMandatorId: state.data.abacusMandator.abacusMandatorId,
          status: state.data.isActive ? 'Active' : 'Inactive',
          isActiveForExternal: state.data.isActiveForExternal,
          deactivationComment: state.data.deactivationComment || '',
          isTermsUpdateRequired: state.data.isTermsUpdateRequired,
        });

        this.customerForm.markAsPristine();
        this.customerSpaceUsers = state.data.users;
        this.customerSpaceRoles = state.data.roles;
        this.projectTitles = state.data.deletionBlockers;
        this.deactivatedBy = state.data.deactivatedBy;
        this.deactivationTime = state.data.deactivationTime;

        this.innerTable?.refereshData(this.customerSpaceUsers);

        // this.openEditEmployeeDialog(this.customerSpaceUsers[0]);
      }
    }

    if (state.status === ResourceStatuses.FAILED) {
      if (state.error?.errors) {
        this.setFormErrors(state.error?.errors);
      }
    }
  }

  private disableErpNumbers() {
    if (!this.isPortalAdministrator) {
      this.customerForm.get('erpAddressNumber')!.disable();
      this.customerForm.get('erpProjectNumber1')!.disable();
      this.customerForm.get('erpProjectNumber2')!.disable();
      this.customerForm.get('erpProjectNumber3')!.disable();
      this.customerForm.get('erpProjectNumber4')!.disable();
    }
  }

  private setFormErrors(errors: Error) {
    if (errors['Name']) {
      this.customerForm.get('name')!.setErrors({
        backendError: errors['Name'][0],
      });
    }

    if (errors['AbacusMandatorId']) {
      this.customerForm.get('abacusMandatorId')!.setErrors({
        backendError: errors['AbacusMandatorId'][0],
      });
    }
  }
}
